<template>
  <div class="grid-container">
    <template v-if="isEditing">
      <!--
          We only want to render this below when we're editing. Removes some of the unnecessary items from view.
        -->
      <div class="c-grid-editing">
        <sc-text tag="h3" v-if="fields && fields.title" :field="fields.title" />
        <sc-rich-text v-if="fields && fields.leadCopy" :field="fields.leadCopy" />
        <sc-placeholder name="jss-grid-item" :rendering="rendering" />
        <sc-rich-text v-if="fields && fields.endCopy" :field="fields.endCopy" />
      </div>
    </template>
    <template v-else>
      <!--
          We only want to render this below when we're not editing.
        -->
      <sc-text tag="h3" v-if="fields && fields.title" :field="fields.title" />
      <sc-rich-text v-if="fields && fields.leadCopy" :field="fields.leadCopy" />
      <div class="c-grid" :class="[{ twoColumn: fields?.twoColumn?.value === true }]">
        <sc-placeholder name="jss-grid-item" :rendering="rendering" />
      </div>
      <sc-rich-text v-if="fields && fields.endCopy" :field="fields.endCopy" />
    </template>
  </div>
</template>

<script>
import { Text, RichText, Placeholder } from '@sitecore-jss/sitecore-jss-vue';

export default {
  name: 'CIS-Grid',
  components: {
    ScText: Text,
    ScRichText: RichText,
    ScPlaceholder: Placeholder
  },
  props: {
    fields: {
      type: Object,
      default: () => ({})
    },
    rendering: {
      type: Object
    }
  },
  computed: {
    isEditing() {
      // this.$jss is defined on the Vue instance by the SitecoreJssPlugin and provides
      // reactive access to the `sitecoreContext` provided in layout service data.
      return this.$jss.sitecoreContext().pageEditing;
    }
  }
};
</script>
<style lang="scss" scoped>
.grid-container {
  margin: 4rem 0 5rem 0;
}
.c-grid {
  @include tablet-up {
    display: flex;
    flex-wrap: wrap;
    margin: auto;
  }

  &:deep() {
    padding-bottom: 1rem;

    @include desktop-up {
      padding-top: 2rem;
      padding-bottom: 0;
    }

    .c-grid__title {
      text-align: center;
      margin-bottom: 5rem;
      -webkit-text-fill-color: unset;
      @include font-styles('Arial', 2.4rem, 200, normal, 3rem, 0, $white);

      @include desktop-up {
        font-size: 3.6rem;
        line-height: 4.2rem;
      }
    }

    .c-grid__item {
      display: flex;
      flex-direction: column;
      flex-wrap: nowrap;
      position: relative;
      background: $white;
      padding-bottom: 2rem;
      margin-bottom: 2rem;
      @include add-Shadow;
      margin: 0 0 4rem 0;

      @include tablet-up {
        width: calc(100% / 2 - 1rem);
      }

      @include desktop-up {
        width: calc(100% / 3 - 2rem);
      }

      @include large-desktop-up {
        width: calc(100% / 3 - 4rem);
      }

      &:nth-child(2n + 2) {
        @include tablet-up {
          margin-left: 2rem;
        }

        @include desktop-up {
          margin-left: 0;
        }
      }

      &:nth-child(3n + 2) {
        @include desktop-up {
          margin: 0 3rem 4rem 3rem;
        }

        @include large-desktop-up {
          margin: 0 6rem 4rem 6rem;
        }
      }

      .grid-title {
        @include font-styles('Arial', 2rem, 400, normal, 2.6rem, 0, $dark-blue);
        padding: 1rem 2rem 0 2rem;
      }

      .grid-content {
        font-size: 2rem;
        font-weight: 200;
        line-height: 2.6rem;
        height: 100%;
        padding: 1rem 2rem 6rem 2rem;
        color: $dark-blue;

        &.ctaVisible {
          padding-bottom: 0.9rem;
        }

        a {
          font-size: 2rem;
          font-weight: 300;
          line-height: 2.6rem;

          &:hover {
            text-decoration: underline;
          }
        }
      }

      .c-cta--secondary {
        width: fit-content;
        margin: 0 2rem;
      }

      .grid-expand {
        position: absolute;
        bottom: 0.75rem;
        right: 0;
        width: 1.4rem;
        height: 1.4rem;
        background: transparent url(~@/assets/images/icon-expand.svg) no-repeat 50%;
        border: none;
        margin: 0;
        padding: 2rem;
        z-index: 1;
        @include tablet-up {
          bottom: -0.25rem;
          right: -0.25rem;
        }
        @include desktop-up {
          bottom: 0.75rem;
          right: 0;
        }
      }
    }
  }

  &.twoColumn {
    &:deep() {
      .c-grid__item {
        margin: 0 0 4rem 0;
        @include tablet-up {
          width: calc(100% / 2 - 1rem);
        }

        @include desktop-up {
          width: calc(100% / 2 - 2.4rem);
        }

        &:nth-child(odd) {
          @include tablet-up {
            margin-right: 1rem;
          }

          @include desktop-up {
            margin-right: 2.4rem;
          }
        }

        &:nth-child(even) {
          @include tablet-up {
            margin-left: 1rem;
          }

          @include desktop-up {
            margin-left: 2.4rem;
          }
        }
      }
    }
  }
}

// Editing View
.c-grid-editing {
  &:deep() {
    .c-grid__item {
      padding: 2rem;
      border-top: 0.4rem solid $orange;
    }

    .grid-expand {
      display: none;
    }

    .c-modal {
      border: solid 0.1rem $gray;
      padding: 2rem;
    }

    .c-modal__close {
      display: none;
    }
  }
}
</style>
